import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const emergentAuthUrl = () => {
  // Land on /admin/login so the session_id fragment is handled BEFORE
  // RequireAdmin's protected-route guard runs (which would strip the hash
  // when redirecting an unauthenticated user).
  const redirectUrl = `${window.location.origin}/admin/login`;
  return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AdminLogin() {
  const { login, loginWithGoogleSession, token, checking } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [processingGoogle, setProcessingGoogle] = useState(false);
  const googleProcessedRef = useRef(false);

  // On mount, detect session_id in URL fragment from Emergent Auth callback.
  // Runs synchronously via ref to avoid StrictMode double-invocation issues.
  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=")) return;
    if (googleProcessedRef.current) return;
    googleProcessedRef.current = true;

    // Extract session_id from fragment.
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    // Clean the hash from the URL immediately so a refresh doesn't retry.
    window.history.replaceState(null, "", window.location.pathname);
    if (!sessionId) return;

    setProcessingGoogle(true);
    loginWithGoogleSession(sessionId)
      .then(() => {
        toast.success("Signed in with Google");
        nav("/admin", { replace: true });
      })
      .catch((err) => {
        toast.error(formatApiError(err, "Google sign-in failed"));
      })
      .finally(() => setProcessingGoogle(false));
  }, [loginWithGoogleSession, nav]);

  if (!checking && token && !processingGoogle) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const startGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = emergentAuthUrl();
  };

  const inputCls =
    "w-full bg-[#121212] border border-white/15 rounded-none px-4 py-3.5 text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00] transition-colors";

  return (
    <div className="min-h-screen bg-[#121212] text-white flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-12 h-12 bg-[#FF6B00] flex items-center justify-center">
            <span className="font-display text-2xl leading-none text-white">LC</span>
          </div>
          <div>
            <div className="font-display uppercase text-2xl tracking-wider">LC Builders</div>
            <div className="text-[11px] tracking-[0.3em] text-white/50">ADMIN PANEL</div>
          </div>
        </div>

        {processingGoogle && (
          <div
            data-testid="admin-login-google-processing"
            className="border border-[#FF6B00]/40 bg-[#FF6B00]/10 text-white/90 text-sm p-4 mb-6 inline-flex items-center gap-2 w-full"
          >
            <Loader2 size={14} className="animate-spin" />
            Finishing Google sign-in…
          </div>
        )}

        <form onSubmit={submit} className="border border-white/10 bg-[#1A1D21] p-8" data-testid="admin-login-form">
          <h1 className="font-display uppercase text-3xl tracking-wide mb-2">Sign In</h1>
          <p className="text-white/60 text-sm mb-8">Manage site content and estimate leads.</p>

          <button
            type="button"
            onClick={startGoogleSignIn}
            disabled={processingGoogle || submitting}
            data-testid="admin-login-google"
            className="w-full inline-flex items-center justify-center gap-3 bg-white text-[#121212] hover:bg-white/90 disabled:opacity-60 transition-colors px-6 py-3.5 font-display tracking-widest uppercase text-sm border border-white"
          >
            <GoogleGlyph />
            Sign in with Google
          </button>

          <div className="flex items-center gap-3 my-6" aria-hidden="true">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] tracking-[0.35em] uppercase text-white/40">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <label className="block text-xs tracking-widest uppercase text-white/60 mb-2">Email</label>
          <input
            data-testid="admin-login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            placeholder="you@example.com"
            required
            autoComplete="email"
          />

          <label className="block text-xs tracking-widest uppercase text-white/60 mt-6 mb-2">Password</label>
          <input
            data-testid="admin-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
            placeholder="••••••••"
            required
            autoComplete="current-password"
          />

          <button
            type="submit"
            disabled={submitting || processingGoogle}
            data-testid="admin-login-submit"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#FF6B00] hover:bg-[#E65A00] disabled:opacity-60 transition-colors px-8 py-4 font-display tracking-widest uppercase text-white"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
            Sign In
          </button>

          <p className="text-[11px] text-white/40 mt-6 leading-relaxed">
            Google sign-in only works for pre-authorized admin emails. If you're a new admin, ask an existing admin to add you first (Admin Panel → Admin Users).
          </p>
        </form>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  // Official Google multi-color "G" glyph.
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.257h2.908c1.702-1.567 2.684-3.874 2.684-6.614z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.186l-2.908-2.257c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
    </svg>
  );
}
